import BaseObjectStore from './base/base-object-store.js'

// TENANT-5440: reactive gate state for the web ToS & Privacy Policy consent
// modal (`src/app/modals/consent-modal`). The modal itself is rendered ONCE,
// globally, in `main.jsx`'s App chrome (same pattern as AuthModal /
// ContactSupportModal), reacting to `isRequired()` — so any code path that
// discovers a stale/missing legal acceptance (useVerificationGuard, the
// complete-profile flow) just calls `require()` here instead of mounting its
// own copy of the modal.
//
// `require()` returns a promise that resolves once the user has accepted and
// the acceptance has been persisted (`markAccepted()`), so flows that must
// not proceed until consent is recorded (e.g. complete-profile → opt-in
// navigation) can simply `await` it. Guards that surface the modal over an
// already-loading page can call it fire-and-forget: the modal is
// non-dismissible, so the page underneath stays blocked until accept.
// TENANT-5440 QA fix: `pending` is the third gate state — UNKNOWN. On a fresh
// page load with a token, a guarded page's acceptance status is not known
// until `useVerificationGuard`'s async `GET /user` resolves; during that
// window the page must not be interactive (a fresh Google-SSO user could
// otherwise click around /account or /inbox for a few seconds before the
// consent modal appeared). `useVerificationGuard` flips `pending` on
// synchronously before its first await, and `main.jsx` renders a
// full-viewport blocking scrim while it is set — never the modal itself, so
// there is no show-then-hide flash for users whose acceptance is current.
//
// `resolvedOnce` is deliberately in-memory (not persisted): after the FIRST
// resolution of a page-load, later client-side navigations trust the store
// (acceptance is patched on accept), so `beginStatusCheck()` becomes a no-op
// and navigation never flashes the scrim. Only a fresh page load starts
// UNKNOWN again.
class LegalAcceptanceStore extends BaseObjectStore {
    constructor () {
        super({
            busy: false,
            pending: false,
            required: false,
        })
        this.acceptResolvers = []
        this.resolvedOnce = false
    }

    isPending () {
        return this.get('pending')
    }

    // Called synchronously at the top of `useVerificationGuard`, before its
    // async status fetch. No-op once the status has been resolved this
    // page-load (see `resolvedOnce` above).
    beginStatusCheck () {
        if (this.resolvedOnce || this.get('pending')) return
        this.setState({
            pending: true,
        })
    }

    // The acceptance status is now KNOWN (current → normal page; stale → the
    // caller fired `require()` in the same tick, so the modal replaces the
    // scrim with no interactive gap). Later navigations skip the scrim.
    resolveStatusCheck () {
        this.resolvedOnce = true
        this.setState({
            pending: false,
        })
    }

    // The status check errored: drop the scrim (an eternal scrim over a dead
    // page is worse than the pre-existing error surface) but do NOT mark the
    // status resolved — the next guarded navigation re-checks.
    abortStatusCheck () {
        if (!this.get('pending')) return
        this.setState({
            pending: false,
        })
    }

    isRequired () {
        return this.get('required')
    }

    isBusy () {
        return this.get('busy')
    }

    setBusy (busy) {
        this.setState({
            busy,
        })
    }

    require () {
        if (!this.get('required')) {
            this.setState({
                required: true,
            })
        }
        return new Promise((resolve) => {
            this.acceptResolvers.push(resolve)
        })
    }

    // Called by the App-level accept handler AFTER the POST succeeded. Hides
    // the modal and releases every pending `require()` awaiter. Never called
    // on failure — the modal stays up and the error surfaces via errorStore.
    markAccepted () {
        // Accepting IS a resolution — e.g. the complete-profile flow records
        // acceptance before any guarded page ever runs its status check.
        this.resolvedOnce = true
        this.setState({
            busy: false,
            pending: false,
            required: false,
        })
        const resolvers = this.acceptResolvers
        this.acceptResolvers = []
        for (const resolve of resolvers) {
            resolve()
        }
    }
}

const legalAcceptanceStore = new LegalAcceptanceStore()
export default legalAcceptanceStore
